// cribbed from calling-remote-app/navigation/components/FirstPartyCall
import { useCallback, useEffect, useMemo } from 'react';
import { NavMarker } from 'react-rhumb';
import { useUserInfo } from 'calling-ui-library/user-info/context/UserInfoContext';
import { getStandaloneConnectionId, isTwilioDeviceRegistering } from 'calling-cross-tab-library/appData/operators/widgetDetailsOperators';
import { useWidgetDetails, useCallSettings } from 'worker-connection-library/hooks/useWidgetDetails';
import { useCallingOnboarding } from '../hooks/useCallingOnboarding';
import { useCallingSettingsFetch } from 'calling-ui-library/calling-remote-redesign/hooks/useCallingSettingsFetch';
import { useHandleBidirectionalCallStates } from '../../common/hooks/useHandleBidirectionalCallStates';
import { useOpenRemoteOnCallAnswered } from '../hooks/useOpenRemoteOnCallAnswered';
import { useOpenRemoteOnDeviceError } from '../hooks/useOpenRemoteOnDeviceError';
import { useOpenRemoteOnTransferRequest } from '../hooks/useOpenRemoteOnTransferRequest';
import { useShowAlertOnCallTabClose } from '../hooks/useShowAlertOnCallTabClose';
import { useHasConnectionError } from 'calling-ui-library/calling-remote-redesign/hooks/useHasConnectionError';
import { useShowOfflineAlert } from 'calling-ui-library/calling-remote-redesign/hooks/useShowOfflineAlert';
import { useHasCriticalError } from 'calling-ui-library/calling-remote-redesign/hooks/useHasCriticalError';
import { CallIconStatus } from 'navigation-components/config';
import { useInvokeCapability } from 'navigation-components/consumer/hooks';
import { capabilities } from 'navigation-components/capabilities';
import { CallingRemoteWrapperRedesign } from '../../calling-remote-redesign/components/CallingRemoteWrapperRedesign';
import { useOpenRemoteOnLiveRecommendation } from '../hooks/useOpenRemoteOnLiveRecommendation';
import { useOpenTransferCsat } from '../hooks/useOpenTransferCsat';
import { useOpenDialerCsat } from '../hooks/useOpenDialerCsat';
import { useShowMicrophoneErrorAlert } from 'calling-ui-library/calling-remote-redesign/hooks/useShowMicrophoneErrorAlert';
import { useOpenRemoteOnIncomingCall } from '../hooks/useOpenRemoteOnIncomingCall';
import { useConnectionStatus } from 'worker-connection-library/hooks/useConnectionStatus';
import { ConnectionStatusTypes } from 'shared-worker-versioning/lib/types/ConnectionStatuses';
import { ApolloProvider } from '@apollo/client';
import { createApolloClient } from 'hubspot-apollo-client';
// DFC
import { DataFetchingClient, DataFetchingClientProvider } from 'data-fetching-client';
import ProviderSelect from 'calling-remote-common/components/ProviderSelect';
import { useHandleOpenRemoteMessage } from '../hooks/useHandleOpenRemoteMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const client = createApolloClient();
export function FirstPartyCallRedesign(props) {
  const {
    gates
  } = useUserInfo();
  const {
    state: {
      isOpen
    }
  } = props;
  const openRemote = useInvokeCapability(capabilities.SHOW_COMPONENT_CAP);
  const closeRemote = useInvokeCapability(capabilities.HIDE_COMPONENT_CAP);
  const updateIconStatus = useInvokeCapability(capabilities.UPDATE_CALL_ICON_STATUS_CAP);
  const {
    shouldShowActiveCallState,
    shouldShowEndCallState
  } = useHandleBidirectionalCallStates(gates);
  useOpenRemoteOnCallAnswered(openRemote, shouldShowActiveCallState);
  useOpenRemoteOnIncomingCall(openRemote);
  useShowAlertOnCallTabClose(openRemote);
  useOpenRemoteOnDeviceError(openRemote);
  useOpenRemoteOnTransferRequest(openRemote);
  useOpenRemoteOnLiveRecommendation(openRemote);
  useHandleOpenRemoteMessage(openRemote);

  // CSAT surveys
  useOpenDialerCsat();
  useOpenTransferCsat();
  const {
    isCallingSettingsLoading
  } = useCallingSettingsFetch({
    shouldFetchData: isOpen
  });
  const {
    hasCallingAccess,
    isHubSpotCallingEnabled,
    portalFromNumbers,
    installedCallingSettings
  } = useCallSettings();
  const hasPortalFromNumbers = portalFromNumbers ? portalFromNumbers.length > 0 : false;
  const {
    isLoading: isLoadingCallingOnboarding,
    callingOnboardingStatus,
    setCallingOnboarding
  } = useCallingOnboarding({
    hasCallingAccess: hasCallingAccess === undefined ? null : hasCallingAccess,
    isOpen,
    isLoadingCallingAccess: isCallingSettingsLoading
  });
  const {
    hasConnectionError,
    hasConnectionDeprecated
  } = useHasConnectionError();
  const connectionStatus = useConnectionStatus();
  const showConnectionErrorAlert = useMemo(() => (hasConnectionError || hasConnectionDeprecated) && connectionStatus !== ConnectionStatusTypes.UNSUPPORTED && isHubSpotCallingEnabled && hasPortalFromNumbers, [connectionStatus, hasConnectionDeprecated, hasConnectionError, hasPortalFromNumbers, isHubSpotCallingEnabled]);
  const {
    hasCriticalError
  } = useHasCriticalError();
  const showCriticalErrorAlert = useMemo(() => hasCriticalError && isHubSpotCallingEnabled && hasPortalFromNumbers, [hasCriticalError, hasPortalFromNumbers, isHubSpotCallingEnabled]);
  const {
    showOfflineAlert
  } = useShowOfflineAlert();
  const {
    showMicrophoneErrorAlert
  } = useShowMicrophoneErrorAlert();
  const {
    widgetDetails
  } = useWidgetDetails();
  const isTwilioLoading = isTwilioDeviceRegistering(widgetDetails);
  const standaloneConnectionId = getStandaloneConnectionId(widgetDetails);

  // TODO: Remove all the setIsOpen handlers from hooks and create a useOpenRemote hook that has all the logic in one place
  useEffect(() => {
    // Only show alert if there is a token error and we have a standalone connection id
    if (showOfflineAlert || showMicrophoneErrorAlert || showCriticalErrorAlert || showConnectionErrorAlert) {
      openRemote();
    }
  }, [openRemote, showMicrophoneErrorAlert, showOfflineAlert, standaloneConnectionId, showCriticalErrorAlert, showConnectionErrorAlert]);
  useEffect(() => {
    if ((showConnectionErrorAlert || showCriticalErrorAlert) && isHubSpotCallingEnabled || showOfflineAlert || showMicrophoneErrorAlert) {
      updateIconStatus({
        status: CallIconStatus.ERROR
      });
    } else if (standaloneConnectionId !== undefined) {
      updateIconStatus({
        status: CallIconStatus.ONLINE
      });
    } else if (standaloneConnectionId === undefined) {
      updateIconStatus({
        status: CallIconStatus.OFFLINE
      });
    }
  }, [isHubSpotCallingEnabled, showConnectionErrorAlert, showCriticalErrorAlert, showMicrophoneErrorAlert, showOfflineAlert, standaloneConnectionId, updateIconStatus]);
  useEffect(() => {
    if (shouldShowActiveCallState) {
      updateIconStatus({
        shouldShowActiveCallState: true
      });
    } else {
      updateIconStatus({
        shouldShowActiveCallState: false
      });
    }
    if (shouldShowEndCallState) {
      updateIconStatus({
        shouldShowEndCallState: true
      });
    } else {
      updateIconStatus({
        shouldShowEndCallState: false
      });
    }
  }, [shouldShowActiveCallState, shouldShowEndCallState, updateIconStatus]);
  const ProviderSelectComponent = useCallback(({
    location
  }) => {
    if (!installedCallingSettings || installedCallingSettings.length === 0) {
      return null;
    }
    return /*#__PURE__*/_jsx(ProviderSelect, {
      installedCallingSettings: installedCallingSettings,
      location: location
    });
  }, [installedCallingSettings]);
  const showLoadingSpinner = isCallingSettingsLoading || isLoadingCallingOnboarding || isTwilioLoading;
  const dataFetchingClient = new DataFetchingClient();
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "INDEX_LOAD"
    }), /*#__PURE__*/_jsx(ApolloProvider, {
      client: client,
      children: /*#__PURE__*/_jsx(DataFetchingClientProvider, {
        client: dataFetchingClient,
        children: isOpen ? /*#__PURE__*/_jsx(CallingRemoteWrapperRedesign, {
          closeRemote: closeRemote,
          showLoadingSpinner: showLoadingSpinner,
          callingOnboardingStatus: callingOnboardingStatus,
          setCallingOnboarding: setCallingOnboarding,
          providerSelectComponent: ProviderSelectComponent
        }) : null
      })
    })]
  });
}